// @flow

import { random } from '@a1s/lib';

import React, { isValidElement, type Node, PureComponent } from 'react';

import styled from 'styled-components';

import Backdrop from './Backdrop';
import Menu from './Menu';
import Opener from './Opener';

import type { OpenerType } from './OpenerIcon';
import { Context } from './Provider';

const Container = styled.div`
  display: inline-block;
  position: relative;
`;

export type DirectionTypes = 'down' | 'up';

type Props = {
  ariaLabel?: string,
  children: Node,
  collapse?: boolean,
  dataTestId?: string,
  direction?: DirectionTypes,
  name?: string,
  opener?: OpenerType,
  reverseContrast?: boolean,
};

type State = { open: boolean };

export default class PopupMenu extends PureComponent<Props, State> {
  state = { open: false };

  menuId: string = `id${random()}`;

  componentDidUpdate() {
    const { openMenuId, setOpenMenuId } = this.context;

    // If we're not within a `Context`, don't worry about updates.
    if (typeof setOpenMenuId !== 'function') return;

    const { open } = this.state;

    if (open === true && openMenuId !== this.menuId) {
      // Overwrite ESLint here. Since we're checking for `open` we're avoiding an infinite loop.
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ open: false });
    }
  }

  handleClick = (event: SyntheticMouseEvent<>) => {
    event.preventDefault();
    event.stopPropagation();

    const { setOpenMenuId } = this.context;
    const { open } = this.state;

    // If the menu is open, tell the context that a menu isn't open. If the menu isn't open
    // tell the context which menu has been opened.
    if (setOpenMenuId) setOpenMenuId(open ? null : this.menuId);

    this.setState({ open: !open });
  };

  static contextType = Context;

  render() {
    const {
      ariaLabel,
      children,
      collapse = false,
      dataTestId = 'molecule-popup-menu',
      direction = 'down',
      name: openerName,
      opener = 'more',
      reverseContrast = false,
    } = this.props;

    const { open } = this.state;

    const isOpen =
      ((Array.isArray(children) && children.length > 0) || (isValidElement(children) && isValidElement !== null)) &&
      open;

    return (
      <>
        {isOpen && <Backdrop onClick={this.handleClick} />}
        <Container data-testid={dataTestId} opener={opener}>
          <Opener
            aria-label={ariaLabel}
            expanded={isOpen}
            menuId={this.menuId}
            name={openerName}
            onClick={this.handleClick}
            opener={opener}
            reverseContrast={reverseContrast}
          />
          <Menu
            aria-hidden={String(!isOpen)}
            collapse={collapse}
            direction={direction}
            id={this.menuId}
            onClick={this.handleClick}
          >
            {children}
          </Menu>
        </Container>
      </>
    );
  }
}

export { default as Group } from './Group';
export { default as Option } from './Option';
export { default as PopupMenuProvider } from './Provider';
